import { useEffect, useState } from 'react'

import { queryClient } from '@/providers/QueryClientProvider'
import { useMutation, useQuery } from '@api/hooks'
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog'
import { MarketBasket, MarketBasketExclusion, MarketBasketStatus } from '@domain'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Stack, Typography } from '@mui/material'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { ProgramsKeys } from '@services/programs.api'
import { useDialogs } from '@toolpad/core'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import { ManualExclusionsFormValues, manualExclusionsSchema } from './BasketExclusions.schema'
import ManualExclusionsForm from './ManualExclusionsForm'
import { SalesforceExclusionTable } from './SalesforceExclusionsTable'

type Props = {
	open: boolean
	onClose: (values?: MarketBasketExclusion[]) => void
}

/**
 * Displays a drawer with a baskets exclusions.
 */
export default function BasketExclusions({ open, onClose }: Props) {
	const [updatedSalesforceExclusions, setUpdatedSalesforceExclusions] = useState<
		MarketBasketExclusion[]
	>([])

	const [, setCustomer] = useQueryParam('exclusionCustomer', StringParam)
	const [, setManufacturer] = useQueryParam('exclusionManufacturer', StringParam)
	const [, setEnabled] = useQueryParam('exclusionEnabled', StringParam)
	const [, setProgram] = useQueryParam('exclusionProgram', StringParam)

	const { marketBasketId } = useParams()
	const dialogs = useDialogs()

	const { data: manualExclusions, isLoading: isLoadingManualExclusions } = useQuery({
		queryKey: MarketBasketsKeys.manualExclusions(marketBasketId),
		queryFn: () => MarketBasketsApi.manualExclusions(marketBasketId),
		enabled: !!marketBasketId,
	})

	const { mutate } = useMutation({
		mutationFn: MarketBasketsApi.updateExclusions,
		onMutate: () => {
			// Optimistically update the market basket status to recalculating
			queryClient.setQueryData(
				MarketBasketsKeys.marketBasket(marketBasketId),
				(oldData: MarketBasket) => ({
					...oldData,
					status: MarketBasketStatus.RECALCULATING,
				})
			)
		},

		// Invalidate queries onSuccess to ensure correct data is fetched after mutation
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: ProgramsKeys.all,
			})
		},
	})

	const methods = useForm<ManualExclusionsFormValues>({
		mode: 'onBlur',
		resolver: zodResolver(manualExclusionsSchema),
		shouldFocusError: true,
	})

	useEffect(() => {
		// update RHF form with the data from the API, if editing an existing basket
		if (!isLoadingManualExclusions && marketBasketId) {
			methods.reset({ manualExclusions })
		}
	}, [manualExclusions, isLoadingManualExclusions, marketBasketId, methods])

	const clearParams = () => {
		setCustomer(undefined)
		setManufacturer(undefined)
		setEnabled(undefined)
		setProgram(undefined)
	}

	const handleSubmit: SubmitHandler<ManualExclusionsFormValues> = ({ manualExclusions }) => {
		onClose()
		setUpdatedSalesforceExclusions([])
		clearParams()
		const manualExclusionsChanged = methods.formState.isDirty
		return mutate({
			id: marketBasketId,
			exclusions: {
				manualExclusions: manualExclusionsChanged ? manualExclusions : [],
				salesforceExclusions: updatedSalesforceExclusions,
			},
		})
	}

	const handleClose = async () => {
		if (!methods.formState.isDirty && !updatedSalesforceExclusions.length) {
			setUpdatedSalesforceExclusions([])
			clearParams()
			onClose()
			return
		}

		const close = await dialogs.open(ConfirmDialog, {
			title: 'Discard Changes',
			description: 'You have unsaved changes, close without saving?',
			confirmLabel: 'Yes',
			rejectLabel: 'No',
		})

		if (close) {
			methods.reset({ manualExclusions })
			setUpdatedSalesforceExclusions([])
			clearParams()
			onClose()
		}
	}

	return (
		<Drawer open={open} variant="temporary" anchor="right" onClose={handleClose}>
			<Stack spacing={2} sx={{ width: '73.5625rem', p: 2 }}>
				<Stack spacing={2} direction="row" sx={{ width: '100%', mb: 2 }}>
					<Typography variant="h6">Exclusions</Typography>
					<Stack spacing={2} direction="row" sx={{ width: '100%', justifyContent: 'flex-end' }}>
						<Button onClick={() => onClose()} color="secondary">
							Cancel
						</Button>
						<Button
							disabled={!updatedSalesforceExclusions.length && !methods.formState.isDirty}
							type="submit"
							form="basket-exclusions"
							variant="contained"
						>
							Save
						</Button>
					</Stack>
				</Stack>
				<FormProvider {...methods}>
					<form id="basket-exclusions" onSubmit={methods.handleSubmit(handleSubmit)}>
						{!isLoadingManualExclusions && <ManualExclusionsForm />}
					</form>
					{isLowerEnvironment && <DevTool control={methods.control} placement="top-right" />}
				</FormProvider>
				<SalesforceExclusionTable
					updatedSalesforceExclusions={updatedSalesforceExclusions}
					setUpdatedSalesforceExclusions={setUpdatedSalesforceExclusions}
				/>
			</Stack>
		</Drawer>
	)
}
